import React from 'react';
import PropTypes from 'prop-types';
import { ChevronRight } from 'lucide-react';
import Avatar from '../ui/Avatar.jsx';
import ListRow from '../ui/ListRow.jsx';
import Pill from '../ui/Pill.jsx';

const skillNames = list => (Array.isArray(list) ? list : [])
  .map(skill => (typeof skill === 'string' ? skill : skill?.name || ''))
  .filter(Boolean);

/**
 * Warum passt diese Person zur Suche? Liefert den Grund fuer die Meta-Zeile und
 * den Skill, der als lila Pille rechts steht.
 */
export const describeMatch = (profile, searchTerm = '') => {
  const offered = skillNames(profile.tenant_skills_OfferedSkills || profile.offeredSkills || profile.offers);
  const sought = skillNames(profile.tenant_skills_SoughtSkills || profile.soughtSkills || profile.seeks);
  const term = searchTerm.trim().toLowerCase();

  if (term) {
    const helps = offered.find(s => s.toLowerCase().includes(term));
    if (helps) return { reason: 'Kann helfen bei', skill: helps };
    const learns = sought.find(s => s.toLowerCase().includes(term));
    if (learns) return { reason: 'Möchte lernen', skill: learns };
  }
  if (offered.length > 0) return { reason: `kann ${offered.slice(0, 2).join(', ')}`, skill: null };
  if (sought.length > 0) return { reason: `möchte ${sought.slice(0, 2).join(', ')} lernen`, skill: null };
  return { reason: 'Noch keine Skills eingetragen', skill: null };
};

/**
 * Meta-Zeilen unter dem Namen (Okt 2026): erst „Klasse 12“, darunter „Kann helfen
 * bei [Skill]“. Der passende Skill steht als Pille IM Grund, nicht mehr rechts:
 * Rechts liess eine lange Pille („Tanzen & Choreografie“) auf 320 px nur ~100 px
 * fuer Name und Klasse uebrig („Lena …“, „Klasse …“). Die Klasse hat eine eigene
 * Zeile, deshalb kein „·“ mehr, das je nach Breite am Zeilenende haengen bliebe.
 * Ist neben „Kann helfen bei“ zu wenig Platz (Pille unter 6rem), rutscht die Pille
 * als Ganzes in die naechste Zeile, statt auf „T…“ zu schrumpfen.
 */
const MetaLine = ({ grade, reason, skill }) => (
  <span className="flex flex-col gap-y-1 whitespace-normal">
    {grade && <span className="truncate">{`Klasse ${grade}`}</span>}
    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
      <span className={skill ? 'whitespace-nowrap' : 'min-w-0 max-w-full truncate'}>{reason}</span>
      {skill && ' '}
      {skill && <Pill size="sm" match className="min-w-[6rem] max-w-full">{skill}</Pill>}
    </span>
  </span>
);

MetaLine.propTypes = {
  grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reason: PropTypes.string.isRequired,
  skill: PropTypes.string,
};

/**
 * Trefferzeile der Suche (Okt 2026, wie im Werbespot, Motion-Kit `ui.search`):
 * Avatar 44 px, Name, „Klasse 12 · Kann helfen bei“ und rechts der passende
 * Skill als lila Pille. Vorher zeigte die Suche ganze Profilkarten im Raster —
 * schoen, aber nach drei Treffern war der Bildschirm voll.
 */
const PersonRow = ({ profile, searchTerm, onOpen }) => {
  const name = profile.displayName || profile.name || 'Unbekannt';
  const grade = profile.grade || profile.schoolClass;
  const { reason, skill } = describeMatch(profile, searchTerm);
  return (
    <ListRow
      onClick={() => onOpen(profile)}
      aria-label={`Profil von ${name} ansehen`}
      leading={<Avatar name={name} src={profile.avatar || profile.image || undefined} size="row" />}
      title={name}
      meta={<MetaLine grade={grade} reason={reason} skill={skill} />}
      trailing={<ChevronRight className="h-4 w-4 text-faint" aria-hidden="true" />}
    />
  );
};

PersonRow.propTypes = {
  profile: PropTypes.object.isRequired,
  searchTerm: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
};

export default PersonRow;
